import React, { useCallback, useId, useMemo } from 'react';
import type {
  GridFilterInputValueProps,
  GridSlotProps,
} from '@mui/x-data-grid-pro';
import { useGridApiContext, useGridRootProps } from '@mui/x-data-grid-pro';
import { IntlShape, useIntl } from 'react-intl';

type FilterInputRootSlotProps = {
  label?: string;
  placeholder?: string;
};

interface FilterOption {
  value: string | number;
  label: string;
}

export const createFixedOptionsFilterInput = (
  getOptions: (intl: IntlShape) => FilterOption[]
) => {
  function FixedOptionsFilterInput(
    props: GridFilterInputValueProps
  ): React.ReactElement | null {
    const { item, applyValue, focusElementRef, tabIndex, slotProps } =
      props;
    const dataGridApiRef = useGridApiContext();
    const rootProps = useGridRootProps();
    const intl = useIntl();
    const id = useId();
    const labelId = useId();
    const options = useMemo(() => getOptions(intl), [intl]);
    const firstOptionValue = options[0]?.value ?? '';

    const filterValue = item.value ?? '';
    const displayValue =
      filterValue === '' && options.length > 0
        ? options[0].value
        : filterValue;

    const onFilterChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const raw = event.target.value;
        const value = raw || firstOptionValue;
        applyValue({ ...item, value });
      },
      [firstOptionValue, applyValue, item]
    );

    const rootSlotProps =
      ((slotProps?.root ?? {}) as Partial<GridSlotProps['root']> &
        FilterInputRootSlotProps);

    const baseSelectProps: Partial<GridSlotProps['baseSelect']> =
      rootProps.slotProps?.baseSelect ?? {};
    const baseSelectOptionProps: Partial<
      GridSlotProps['baseSelectOption']
    > = rootProps.slotProps?.baseSelectOption ?? {};

    const label =
      rootSlotProps.label ??
      dataGridApiRef.current.getLocaleText?.('filterPanelInputLabel') ??
      '';

    const BaseSelect = rootProps.slots?.baseSelect;
    const OptionComponent = rootProps.slots?.baseSelectOption;
    const isSelectNative = Boolean(baseSelectProps.native);

    if (!BaseSelect || !OptionComponent) return null;

    return (
      <BaseSelect
        fullWidth
        id={id}
        label={label}
        labelId={labelId}
        value={displayValue}
        onChange={onFilterChange}
        slotProps={{
          htmlInput: {
            tabIndex,
            ref: focusElementRef,
            type: 'text',
            placeholder:
              rootSlotProps.placeholder ??
              dataGridApiRef.current.getLocaleText?.(
                'filterPanelInputPlaceholder'
              ),
          },
        }}
        native={isSelectNative}
        {...baseSelectProps}
      >
        {options.map((option) => (
          <OptionComponent
            {...baseSelectOptionProps}
            native={isSelectNative}
            key={option.value}
            value={option.value}
          >
            {option.label}
          </OptionComponent>
        ))}
      </BaseSelect>
    );
  }

  return FixedOptionsFilterInput;
};
